/* eslint-disable react/react-in-jsx-scope */
import { useLazyQuery, useMutation } from "@apollo/client";
import { Skeleton } from "@material-ui/lab";
import { RouteComponentProps, navigate } from "@reach/router";
import { QuitButton } from "components/Back";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { useCourseContext } from "contexts/CourseContext";
import { Action, ActionType, Course } from "generated/graphql";
import { ALL_CLIENT_REF_ADDRESS_BY_CLIENT_ID } from "graphql/clientRef/queries";
import { ADD_COURSE, EDIT_COURSE } from "graphql/course/mutations";
import { GET_COURSE_BY_ID } from "graphql/course/queries";
import { useCurrentClient } from "hooks";
import { DeletionIcon } from "icons";
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import CancellationDialog from "ui/Dialog/DialogCancellation";
import { AddItem } from "../components/AddItem";
import CustomAccordion from "../components/CustomAccordion";
import Footer from "../components/Footer";
import Delivery from "../components/Steps/Delivery";
import Pickup from "../components/Steps/Pickup";
import ConfirmModal from "../components/modals/ConfirmModal";
import { CourseTypes, ICourseDelivery, ICoursePickup } from "../data/types";
import { AccordionContainer, CourseContainer, DeleteButton, useMaintPageStyle } from "../style";

interface ICourse extends RouteComponentProps {
  course: Course;
}

const CourseMainPage = ({ ...props }) => {
  const {
    currentElement,
    pickups,
    deliveries,
    addPickup,
    addDelivery,
    deleteDelivery,
    deletePickup,
    setCurrentElement,
    initialReset,
    isDeliveryValid,
    isPickupValid,
    setIsSubmitted,
    setPickups,
    setDeliveries,
    setClientAddresses,
    setIsLoading,
    clientAddresses,
    allClients,
  } = useCourseContext();

  const { newCreatedClient } = (props?.location?.state as any) || {};

  const { text } = useText();
  const classes = useMaintPageStyle();
  const { client } = useCurrentClient();
  const dispatchAlert = useAlertDispatch();
  const [isEdit, setIsEdit] = useState(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [createCourse, { loading }] = useMutation(isEdit ? EDIT_COURSE : ADD_COURSE);
  const [editedCourse, setEditedCourse] = useState<any>();
  const [getCourseById] = useLazyQuery(GET_COURSE_BY_ID, {
    onCompleted: (data) => {
      setEditedCourse(data?.results);
    },
    fetchPolicy: "cache-and-network",
  });
  const [skeleton, setSkeleton] = useState([]);

  const [getAddressByClient, { loading: isAddressLoading }] = useLazyQuery(ALL_CLIENT_REF_ADDRESS_BY_CLIENT_ID, {
    onCompleted: ({ results }) => {
      setClientAddresses(results?.data);
    },
  });

  const validPickups = useMemo(
    () =>
      pickups?.map((pickup) => {
        const delay = pickup?.delay?.split("/");
        const items = pickup?.products.map(({ name, code, quantity }) => ({
          description: name,
          code,
          quantity: +quantity,
        }));
        return {
          id: pickup?.id,
          type: ActionType?.Pickup,
          clientId: client?._id,
          address: pickup?.address?.address?.place_id,
          items,
          executionDate: delay && delay[0]?.trim(),
          executionTime: delay && delay[1]?.trim(),
          reference: pickup?.reference,
          receiver: {
            firstname: pickup?.address?.firstname,
            lastname: pickup?.address?.lastname,
            phoneNumber: pickup?.address?.phone_number,
            email: pickup?.address?.email,
            address: {
              place_id: pickup?.address?.address?.place_id,
            },
          },
        };
      }),
    [pickups]
  );

  const validDeliveries = useMemo(
    () =>
      deliveries?.map((delivery) => {
        const delay = delivery?.delay?.split("/");
        const items = delivery?.products?.map(({ name, code, quantity }: any) => ({
          description: name,
          code,
          quantity: +quantity,
        }));
        return {
          id: delivery?.id,
          type: ActionType.Delivery,
          clientId: client?._id,
          address: delivery?.deliveryAddress?.address?.place_id,
          items,
          executionDate: delay && delay[0]?.trim(),
          executionTime: delay && delay[1]?.trim(),
          reference: delivery?.reference,
          receiver: {
            firstname: delivery?.deliveryAddress?.firstname,
            lastname: delivery?.deliveryAddress?.lastname,
            phoneNumber: delivery?.deliveryAddress?.phone_number,
            email: delivery?.deliveryAddress?.email,
            address: {
              place_id: delivery?.deliveryAddress?.address?.place_id,
            },
          },
        };
      }),
    [deliveries]
  );

  const courseInput = useMemo(
    () => ({
      /* ...(id && { id }), */
      clientId: client?._id,
      isIndividual: false,
      individualClientId: null /* !isIndividual ? client?._id : null, */,
      actions: pickups?.length && deliveries?.length && [...validPickups, ...validDeliveries],
    }),
    [pickups, deliveries]
  );

  const handleCurrentAccordion = (index: number) => {
    const isActivated: boolean = currentElement === index;
    setCurrentElement(!isActivated ? index : null);
  };

  const getPickuAddress = useCallback((index) => pickups[index].address?.address?.address, [pickups]);
  const getDeliveryPickuAddress = useCallback(
    (index) => deliveries[index].deliveryAddress?.address?.address,
    [deliveries]
  );

  const handleCreation = () => {
    setIsSubmitted(true);
    if (isPickupValid && isDeliveryValid && pickups?.length <= deliveries?.length) {
      setOpenModal(true);
    }
    if (!isPickupValid || !isDeliveryValid) {
      dispatchAlert({
        message: text.course.pickup_delivery_validation_warning,
        severity: "warning",
      });
    }
    if (isPickupValid && isDeliveryValid && pickups?.length > deliveries?.length) {
      dispatchAlert({
        message: text.course.pickup_delivery_validation_warning,
        severity: "warning",
      });
    }
  };
  const handleCancelCreation = () => {
    isEdit ? navigate(-1) : initialReset();
  };

  const onCloseModal = () => {
    setOpenModal(false);
  };

  const onConfirmAction = () => {
    createCourse({
      variables: {
        ...(isEdit ? { input: courseInput } : { courseInput }),
      },
    })
      .then((data) => {
        dispatchAlert({
          message: isEdit ? text.course.course_edition_success : text.course.course_creation_success,
          severity: "success",
        });
        setOpenModal(false);
        navigate(-1);
      })
      .catch((e) => {
        dispatchAlert({
          message: text.all.something_wrong,
          severity: "error",
        });
      });
  };

  const dateFormater = (date: string) => {
    const datePartitions = date?.split("-");
    return `${datePartitions[2]}-${datePartitions[1]}-${datePartitions[0]}`;
  };

  const editedClient = useMemo(
    () => ({
      clientType: editedCourse?.isIndividual ? CourseTypes.PRIVATE_INDIVIDUAL : CourseTypes.ENTREPRISE,
      individual: editedCourse?.individualClient,
      entreprise: allClients?.find((client) => client?._id === editedCourse?.client?._id),
    }),
    [isEdit, allClients]
  );

  const allEditedPickups = useMemo(() => {
    const editedPickups = editedCourse?.actions?.filter((action: Action) => action?.type === ActionType?.Pickup);

    return editedPickups?.map((pickup: Action) => {
      const products = pickup?.items?.map(({ description, code }) => ({ name: description, code }));
      const address = clientAddresses?.find((item) => item?.address?.place_id === pickup?.address?.place_id);

      return {
        id: pickup._id,
        reference: pickup?.reference,
        products,
        delay: pickup && `${dateFormater(pickup?.executionDate)} / ${pickup?.executionTime}`,
        address,
      };
    });
  }, [isEdit, clientAddresses, allClients]);

  const allEditedDeliveries = useMemo(() => {
    const editedDeliveries = editedCourse?.actions?.filter((action: Action) => action?.type === ActionType?.Delivery);

    return editedDeliveries?.map((delivery: Action) => {
      const products = delivery?.items?.map(({ description, code }) => ({ name: description, code }));
      const pickupAddress = allEditedPickups?.find((item: any) => item?.reference === delivery?.reference)?.address;
      const deliveryAddress = clientAddresses?.find((item) => item?.address?.place_id === delivery?.address?.place_id);

      return {
        id: delivery._id,
        reference: delivery?.reference,
        pickupAddress: {
          ...pickupAddress,
        },
        deliveryAddress,
        delay: `${dateFormater(delivery?.executionDate)} / ${delivery?.executionTime}`,
        products,
      };
    });
  }, [isEdit, clientAddresses, allClients]);

  const handleGoBack = () => navigate(-1);
  const handleOpenCancelModal = () => {
    setOpenCancelDialog(true);
  };

  const handleCloseModal = () => {
    setOpenCancelDialog(false);
  };

  useLayoutEffect(() => {
    if (isEdit) {
      getCourseById({
        variables: {
          id: null,
        },
      });
    }
    setSkeleton(
      Array.from(new Array(editedCourse ? editedCourse?.actions?.length + 1 : pickups?.length + deliveries?.length))
    );
  }, [isEdit]);

  useEffect(() => {
    setIsLoading(isAddressLoading);
    if (isEdit) {
      setPickups(allEditedPickups);
      setDeliveries(allEditedDeliveries);
      setCurrentElement(null);
    }
  }, [isEdit, clientAddresses, allClients]);

  useEffect(() => {
    if (!isEdit) {
      initialReset();
    }
  }, []);

  useEffect(() => {
    client &&
      getAddressByClient({
        variables: {
          params: {
            clientID: client?._id,
          },
        },
      });
    setCurrentElement(0);
  }, [client]);

  return (
    <CourseContainer>
      <div className={classes.quitButton}>
        <QuitButton onClick={handleOpenCancelModal} /> <h4>{text.course.new_course}</h4>
      </div>
      <div className={classes.subContainer}>
        <p className={classes.message}>{text.course.new_course_message}</p>
        <div className={classes.root}>
          {isAddressLoading ? (
            skeleton?.map((item: any) => (
              <AccordionContainer key={item}>
                <Skeleton className={classes.skeleton} width="100%" height={80} />
              </AccordionContainer>
            ))
          ) : (
            <>
              {pickups?.map((item: ICoursePickup, index: number) => (
                <AccordionContainer>
                  <CustomAccordion
                    key={item?.reference}
                    label={
                      <div className={classes.headerContainer}>
                        <span>{text.all.pickup}</span>
                        {currentElement !== index && pickups[index]?.products[0]?.name && (
                          <span className={currentElement !== index ? classes.client : classes.activeClient}>
                            {pickups[index]?.products?.length}
                          </span>
                        )}
                      </div>
                    }
                    currentElement={currentElement}
                    index={index}
                    handleClick={() => handleCurrentAccordion(index)}
                  >
                    <Pickup pickupIndex={index} isEdit={isEdit} />
                  </CustomAccordion>
                  {index > 0 && (
                    <DeleteButton onClick={() => deletePickup(index)}>
                      <DeletionIcon width={20} height={20} />
                    </DeleteButton>
                  )}
                </AccordionContainer>
              ))}

              {currentElement < pickups?.length && currentElement !== null && (
                <AddItem title={text.course.another_pickup} onClick={addPickup} />
              )}

              {deliveries?.map((item: ICourseDelivery, index: number) => (
                <AccordionContainer>
                  <CustomAccordion
                    key={item?.reference}
                    label={
                      <div className={classes.headerContainer}>
                        <span>{text.all.delivery}</span>
                        {currentElement === null && deliveries[index]?.products[0]?.name && (
                          <span className={currentElement !== index ? classes.client : classes.activeClient}>
                            {deliveries[index]?.products?.length}
                          </span>
                        )}
                      </div>
                    }
                    currentElement={currentElement}
                    index={pickups?.length + index}
                    handleClick={() => handleCurrentAccordion(pickups?.length + index)}
                  >
                    <Delivery deliveryIndex={index} isEdit={isEdit} />
                  </CustomAccordion>
                  {deliveries?.length > 1 && (
                    <DeleteButton onClick={() => deleteDelivery(index)}>
                      <DeletionIcon width={20} height={20} />
                    </DeleteButton>
                  )}
                </AccordionContainer>
              ))}
            </>
          )}
          {currentElement > pickups?.length - 1 && currentElement !== null && (
            <AddItem title={text.course.another_delivery} onClick={addDelivery} />
          )}
          <Footer
            handleCreation={handleCreation}
            isLoading={isAddressLoading}
            isEdit={isEdit}
            handleCancelCreation={handleCancelCreation}
          />
        </div>
        <ConfirmModal
          loading={loading}
          open={openModal}
          isEdit={isEdit}
          onClose={onCloseModal}
          onConfirm={onConfirmAction}
        />
      </div>
      <CancellationDialog
        title={text.course.cancel_course_title}
        message={text.course.cancel_course_message}
        open={openCancelDialog}
        onClose={handleCloseModal}
        onConfirm={handleGoBack}
      />
    </CourseContainer>
  );
};

export default memo(CourseMainPage);
